import { useState } from 'react';
import {
  Color, Size, fill, t, timeOffBegunCancelPath, timeOffHoursText,
  type TimeOffBegunCancelAnswer, type TimeOffBegunRow, type TimeOffBegunSection,
} from '@bms2/shared';
import { Button, Textarea } from '../../components/controls';
import { FormFrame } from '../../components/forms/FormFrame';
import { useDateFormat } from '../../shell/ViewerZone';
import { post } from '../records/api';
import { line } from '../settings/SettingsShell';
import { timeOffSpan } from './RequestTimeOff';
import styles from './HrPages.module.css';

/**
 * Cancel Remaining Days under the Employee page's Time Off section (piece 34; decision 13251; T3905): each approved
 * request under way, with a button for HR holding the hire permission. 🔒 The route writes the Adjustment row for the
 * days after today, cancels the events from then on and keeps the reason in the audit row; the days already taken stay.
 */
export function TimeOffBegunControls({ apiBaseUrl = '', section, onCanceled }: { apiBaseUrl?: string; section: TimeOffBegunSection; onCanceled: () => void }) {
  const format = useDateFormat();
  const [canceling, setCanceling] = useState<TimeOffBegunRow | null>(null);
  const [reason, setReason] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const rows = section.rows ?? [];
  if (rows.length === 0 && !said) return null;
  const close = () => {
    setCanceling(null);
    setReason('');
    setRefusal(null);
  };
  const cancel = async () => {
    if (!canceling) return;
    const answer = await post<TimeOffBegunCancelAnswer>(timeOffBegunCancelPath(canceling.requestId), { reason: reason.trim() }, apiBaseUrl);
    if (!answer.ok) return setRefusal(answer.message);
    setSaid(fill(t('hr.timeOffBegun.done'), { hours: timeOffHoursText(answer.data.hoursReturned) }));
    close();
    onCanceled();
  };
  return (
    <div className={styles.fields} data-time-off-begun="true">
      {rows.length > 0 ? <p className={styles.meta}>{t('hr.timeOffBegun.heading')}</p> : null}
      {said ? <p className={styles.meta} role="status" data-time-off-begun-said="true">{said}</p> : null}
      {rows.map((row) => (
        <div key={row.requestId} className={styles.controls} data-time-off-begun-row={row.requestId}>
          <span className={styles.meta}>{line(row.planName, '·', fill(t('hr.timeOffBegun.line'), { span: timeOffSpan(row, format), hours: timeOffHoursText(row.hours), returned: timeOffHoursText(row.hoursReturned) }))}</span>
          <span data-time-off-begun-cancel={row.requestId}>
            <Button size={Size.Small} color={Color.Warning} text={[t('hr.timeOffBegun.cancel')]} actions={[() => { setSaid(null); setCanceling(row); }]} />
          </span>
        </div>
      ))}
      {/* 🔨 Mounted only while open and keyed to the request, so a second request's window never shows the first one's reason. */}
      {canceling ? (
        <FormFrame
          key={canceling.requestId}
          host="window"
          open
          title={t('hr.timeOffBegun.title')}
          subtitle={line(canceling.planName, '·', timeOffSpan(canceling, format))}
          dataForm="time-off-begun-cancel"
          typed={reason !== ''}
          onCancel={close}
          refusal={refusal}
          onDismissRefusal={() => setRefusal(null)}
          act={{ label: t('hr.timeOffBegun.cancel'), onAct: () => cancel(), disabled: reason.trim() === '', disabledReason: t('hr.timeOffBegun.hint') }}
        >
          <p className={styles.meta}>{t('hr.timeOffBegun.hint')}</p>
          <Textarea size={Size.Large} label={t('hr.timeOffBegun.reason')} value={reason} onChange={setReason} required name="timeOffBegunReason" />
        </FormFrame>
      ) : null}
    </div>
  );
}
